import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';
import { FiSave, FiX } from 'react-icons/fi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const JobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = UseToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '', 
    location: 'Chennai, India', 
    type: 'Full-time', 
    experience: '', 
    description: '', 
    requirements: '', 
    responsibilities: '', 
    salary: '', 
    skills: '', 
    order: 0,
    isActive: true
  });

  useEffect(() => {
    if (id) fetchJob();
  }, [id]);

  const fetchJob = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${API_URL}/jobs/${id}`);
      const data = await res.json();
      
      if (data.success) {
        const j = data.data;
        setFormData({
          title: j.title || '',
          location: j.location || 'Chennai, India',
          type: j.type || 'Full-time',
          experience: j.experience || '',
          description: j.description || '',
          requirements: Array.isArray(j.requirements)? j.requirements.join(', ') : '',
          responsibilities: Array.isArray(j.responsibilities)? j.responsibilities.join(', ') : '',
          salary: j.salary || '',
          skills: Array.isArray(j.skills)? j.skills.join(', ') : '',
          order: j.order || 0,
          isActive: j.isActive!== undefined? j.isActive : true
        });
      } else {
        toast.error('Job not found');
        navigate('/jobs');
      }
    } catch (error) {
      toast.error('Failed to load job');
      navigate('/jobs');
    }
    setFetching(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = id? `${API_URL}/jobs/${id}` : `${API_URL}/jobs`;
      
      const res = await fetch(url, {
        method: id? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(id? 'Job updated!' : 'Job created!');
        navigate('/careers/jobs');
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  if (fetching) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <>
      <style>{`
.form-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); max-width: 900px; }
.form-title { font-size: 24px; font-weight: 800; color: #0F172A; margin: 0 0 30px 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 20px; }
.form-group-full { grid-column: 1 / -1; }
.form-label { display: block; font-size: 14px; font-weight: 600; color: #334155; margin-bottom: 8px; }
.form-input,.form-select,.form-textarea { width: 100%; padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 15px; transition: all 0.3s ease; box-sizing: border-box; font-family: inherit; }
.form-input:focus,.form-select:focus,.form-textarea:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
.form-textarea { min-height: 100px; resize: vertical; }
.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 30px; }
.checkbox-group { display: flex; align-items: center; gap: 8px; }
.checkbox-group input[type="checkbox"] { width: 20px; height: 20px; cursor: pointer; }
        @media (max-width: 768px) {.form-grid { grid-template-columns: 1fr; } }
      `}</style>
      <div>
        <div className="form-card">
          <h1 className="form-title">{id? 'Edit Job' : 'Post New Job'}</h1>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Job Title *</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location *</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={formData.location} 
                  onChange={(e) => setFormData({...formData, location: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Job Type *</label>
                <select 
                  className="form-select" 
                  value={formData.type} 
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  required
                >
                  <option>Full-time</option>
                  <option>Part-time</option>
                  <option>Contract</option>
                  <option>Internship</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Experience *</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="2-4 years"
                  value={formData.experience} 
                  onChange={(e) => setFormData({...formData, experience: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Salary</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="5-8 LPA or Negotiable"
                  value={formData.salary} 
                  onChange={(e) => setFormData({...formData, salary: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={formData.order} 
                  onChange={(e) => setFormData({...formData, order: e.target.value})} 
                />
              </div>
            </div>
            
            <div className="form-group-full">
              <label className="form-label">Description *</label>
              <textarea 
                className="form-textarea" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})} 
                required 
              />
            </div>
            
            <div className="form-group-full">
              <label className="form-label">Requirements (comma separated)</label>
              <textarea 
                className="form-textarea" 
                placeholder="React.js, Node.js, 3+ years experience"
                value={formData.requirements} 
                onChange={(e) => setFormData({...formData, requirements: e.target.value})} 
              />
            </div>
            
            <div className="form-group-full">
              <label className="form-label">Responsibilities (comma separated)</label>
              <textarea 
                className="form-textarea" 
                placeholder="Develop features, Code review, Team collaboration"
                value={formData.responsibilities} 
                onChange={(e) => setFormData({...formData, responsibilities: e.target.value})} 
              />
            </div>
            
            <div className="form-group-full">
              <label className="form-label">Skills (comma separated)</label>
              <input 
                type="text" 
                className="form-input" 
                placeholder="JavaScript, React, MongoDB"
                value={formData.skills} 
                onChange={(e) => setFormData({...formData, skills: e.target.value})} 
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Status</label>
              <div className="checkbox-group">
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})} 
                />
                <span>Active (Show on website)</span>
              </div>
            </div>
            
            <div className="form-actions">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => navigate('/careers/jobs')} 
                icon={<FiX />}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" icon={<FiSave />} disabled={loading}>
                {loading? 'Saving...' : id? 'Update' : 'Create'} Job
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default JobForm;